import Icon from '../../../components/AppIcon';
import { ContributionPattern } from '../types';

interface ContributionPatternProps {
  pattern: ContributionPattern;
}

const ContributionPatternAnalysis = ({ pattern }: ContributionPatternProps) => {
  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VT`;
  };

  const getFrequencyIcon = (frequency: ContributionPattern['frequency']) => {
    switch (frequency) {
      case 'daily':
        return 'Calendar';
      case 'weekly':
        return 'CalendarDays';
      case 'monthly':
        return 'CalendarRange';
    }
  };

  const getConsistencyColor = (consistency: number) => {
    if (consistency >= 80) return { bg: 'bg-success', text: 'text-success' };
    if (consistency >= 60) return { bg: 'bg-warning', text: 'text-warning' };
    return { bg: 'bg-error', text: 'text-error' };
  };

  const consistencyColors = getConsistencyColor(pattern.consistency);

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="TrendingUp" size={20} />
        Contribution Pattern Analysis
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="p-4 bg-muted rounded-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name={getFrequencyIcon(pattern.frequency)} size={20} color="var(--color-primary)" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Contribution Frequency</div>
                <div className="text-base font-semibold text-foreground capitalize">
                  {pattern.frequency}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-muted rounded-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <Icon name="DollarSign" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Average Amount</div>
                <div className="text-base font-semibold text-foreground">
                  {formatCurrency(pattern.averageAmount)}
                </div>
              </div>
            </div>
          </div>

          {pattern.preferredDays && pattern.preferredDays.length > 0 && (
            <div className="p-4 bg-muted rounded-sm">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="CalendarCheck" size={18} color="var(--color-primary)" />
                <div className="text-xs text-muted-foreground">Preferred Days</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {pattern.preferredDays.map((day, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-sm"
                  >
                    {day}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-muted rounded-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Target" size={18} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">Consistency Score</span>
            </div>
            <span className={`text-2xl font-bold ${consistencyColors.text}`}>
              {Math.round(pattern.consistency)}%
            </span>
          </div>

          <div className="relative h-4 bg-background rounded-full overflow-hidden mb-4">
            <div
              className={`absolute left-0 top-0 h-full ${consistencyColors.bg} transition-all duration-500`}
              style={{ width: `${pattern.consistency}%` }}
            />
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                {pattern.consistency >= 80 ? 'Excellent' : pattern.consistency >= 60 ? 'Good' : 'Needs Improvement'} contribution consistency
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <span className="text-muted-foreground">
                Based on scheduled vs actual contributions over the last 90 days
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-primary/5 rounded-sm border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-foreground mb-1">Pattern Insights</p>
            <p className="text-muted-foreground">
              Your contribution pattern shows {pattern.frequency} activity with an average of {formatCurrency(pattern.averageAmount)} per contribution.
              {pattern.consistency >= 80 && " Keep up the excellent consistency!"}
              {pattern.consistency >= 60 && pattern.consistency < 80 && " Consider setting reminders to improve consistency."}
              {pattern.consistency < 60 && " Try to maintain a more regular contribution schedule."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributionPatternAnalysis;